















































































































































































































































































































































































































import {
  computed,
  defineComponent,
  PropType,
  ref,
  toRefs
} from '@nuxtjs/composition-api'
import {
  MarketplaceReview,
  MarketplaceReviewType
} from '~/models/marketplace/reviews'
import { format, isBefore, timeDistanceFromNow } from '~/utils/date'
import MarketplaceSellerModalTrigger from '~/components/car/marketplace/seller/MarketplaceSellerModalTrigger.vue'
import CAvatar from '~/components/shared/configurable/image/avatar/CAvatar.vue'
import CStarRating from '~/components/shared/configurable/rating/CStarRating.vue'
import { defineComponentTranslations } from '~/utils/i18n'
import { useI18n } from '~/compositions/i18n'
import { ciPositive } from '~/icons/source/regular/positive'
import { ciNegative } from '~/icons/source/regular/negative'
import CExpandableContent from '~/components/shared/configurable/expand/CExpandableContent.vue'
import { useAppConfirmationModal } from '~/compositions/app-confirmation-modal'
import { handleError } from '~/utils/http'
import { useSnackbar } from '~/compositions/snackbar'
import { useLogger } from '~/compositions/logger'
import { useDeps } from '~/compositions/dependency-container'
import MarketplaceReviewService from '~/services/marketplace/MarketplaceReviewService'
import { ConfirmationModalOptions } from '~/store/modules/shared/app-confirmation-modal/state'
import { useNamespacedStore } from '~/compositions/store'
import { USER_NS, UserState } from '~/store/modules/shared/user/state'
import CBadge from '~/components/shared/configurable/badge/CBadge.vue'
import CCollapse from '~/components/shared/configurable/collapse/CCollapse.vue'
import CTextArea from '~/components/shared/configurable/form/input/CTextArea.vue'
import CFormError from '~/components/shared/configurable/form/CFormError.vue'
import { arraySchemaToObj } from '~/utils/form'
import { toCamelCase } from '~/utils/object'
import UserEditAdminButton from '~/components/car/classifieds/view/partials/seller/UserEditAdminButton.vue'
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons'
import { ciLike } from '~/icons/source/regular/like'
import { ciExclamationCircle } from '~/icons/source/regular/exclamation-circle'
import CReportModal from '~/components/shared/configurable/modal/CReportModal.vue'
import MarketplaceAdminService from '~/services/marketplace/MarketplaceAdminService'
import { fromUnixTime, subDays } from 'date-fns'

const ADVANTAGES_THRESHOLD = 4
const DISADVANTAGES_THRESHOLD = 2
export default defineComponent({
  components: {
    MarketplaceSellerModalTrigger,
    CReportModal,
    UserEditAdminButton,
    CFormError,
    CTextArea,
    CCollapse,
    CBadge,
    CExpandableContent,
    CStarRating,
    CAvatar
  },
  props: {
    review: {
      type: Object as PropType<MarketplaceReview>,
      required: true
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    },
    inModal: {
      type: Boolean,
      required: false,
      default: false
    },
    compact: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  setup(props, { emit }) {
    const { review } = toRefs(props)
    const { t, tc } = useI18n()
    const snackbar = useSnackbar()
    const logger = useLogger()
    const confirm = useAppConfirmationModal()

    const internalLoading = ref(false)
    const replySchema = ref<any>(null)
    const errors = ref({})
    const showReplyForm = ref(false)
    const showEditComment = ref(false)
    const commentEdit = ref(review.value.comment)
    const reply = ref('')
    const reportModalVisible = ref(false)

    const [marketplaceReviewService, marketplaceAdminService] = useDeps(
      MarketplaceReviewService,
      MarketplaceAdminService
    )
    const { getters: userGetters } = useNamespacedStore<UserState>(USER_NS)

    const isAdmin = computed(() => userGetters('isAdmin'))
    const isAnon = computed(() => userGetters('isSingleOrAnon'))

    const advantages = computed(() => {
      const adv: string[] = []
      if (review.value.deliveryRating > ADVANTAGES_THRESHOLD) {
        adv.push(MarketplaceReviewType.DELIVERY)
      }
      if (review.value.supportRating > ADVANTAGES_THRESHOLD) {
        adv.push(MarketplaceReviewType.SUPPORT)
      }
      if (review.value.qualityRating > ADVANTAGES_THRESHOLD) {
        adv.push(MarketplaceReviewType.QUALITY)
      }

      return adv
    })

    const advantagesString = computed(() => {
      if (advantages.value.length) {
        return advantages.value
          .map(a => t(`marketplace_${a}_rating`))
          .join(', ')
      }
      return ''
    })

    const disadvantages = computed(() => {
      const dis: string[] = []
      if (review.value.deliveryRating < DISADVANTAGES_THRESHOLD) {
        dis.push(MarketplaceReviewType.DELIVERY)
      }
      if (review.value.supportRating < DISADVANTAGES_THRESHOLD) {
        dis.push(MarketplaceReviewType.SUPPORT)
      }
      if (review.value.qualityRating < DISADVANTAGES_THRESHOLD) {
        dis.push(MarketplaceReviewType.QUALITY)
      }

      return dis
    })

    const disadvantagesString = computed(() => {
      if (disadvantages.value.length) {
        return disadvantages.value
          .map(a => t(`marketplace_${a}_rating`))
          .join(', ')
      }
      return ''
    })

    const isBuyer = computed(() => {
      return userGetters('marketplaceBuyerId') === review.value.buyer.id
    })

    const isSeller = computed(() => {
      return userGetters('marketplaceSellerId') === review.value.seller.id
    })

    const isViewer = computed(() => {
      return !isSeller.value && !isBuyer.value
    })

    const reviewIsNonPublicAndOlderThanSevenDays = computed(() => {
      if (review.value.isPublic) {
        return false
      }

      const sevenDaysAgo = subDays(new Date(), 7)

      return isBefore(fromUnixTime(review.value.created), sevenDaysAgo)
    })

    const deleteReview = async () => {
      try {
        emit('set-loading', true)
        const { message } = await marketplaceReviewService.deleteReview(
          review.value.id
        )
        snackbar.success(message)
        emit('refetch-reviews')
      } catch (error) {
        handleError(error, snackbar, logger, t)
      } finally {
        emit('set-loading', false)
      }
    }

    const onDeleteClick = () => {
      confirm.show({
        variant: 'danger',
        title: t('delete_review'),
        text: t('delete_review_confirm'),
        confirmButtonText: t('delete'),
        centered: true,
        size: 'md',
        onConfirm: deleteReview
      } as ConfirmationModalOptions)
    }

    const deleteReply = async () => {
      if (!review.value.reply) {
        return
      }
      try {
        emit('set-loading', true)
        const { message } = await marketplaceReviewService.deleteReply(
          review.value.reply.id
        )
        emit('update-review', {
          ...review.value,
          reply: undefined
        })
        snackbar.success(message)
      } catch (error) {
        handleError(error, snackbar, logger, t)
      } finally {
        emit('set-loading', false)
      }
    }

    const onDeleteReplyClick = () => {
      confirm.show({
        variant: 'danger',
        title: t('delete_reply'),
        text: t('delete_reply_confirm'),
        confirmButtonText: t('delete'),
        centered: true,
        size: 'md',
        onConfirm: deleteReply
      } as ConfirmationModalOptions)
    }

    const fetchReplySchema = async () => {
      try {
        internalLoading.value = true

        if (review.value.reply) {
          // in edit mode
          const {
            schema: fetchedSchema
          } = await marketplaceReviewService.getEditReviewReplySchema(
            review.value.reply.id
          )
          reply.value = review.value.reply.comment
          replySchema.value = arraySchemaToObj(fetchedSchema)
        } else {
          // new
          const {
            schema: fetchedSchema
          } = await marketplaceReviewService.getNewReviewReplySchema(
            review.value.id
          )

          replySchema.value = arraySchemaToObj(fetchedSchema)
        }

        showReplyForm.value = true
      } catch (error) {
        handleError(error, snackbar, logger, t)
      } finally {
        internalLoading.value = false
      }
    }

    const onReplySubmit = async () => {
      try {
        internalLoading.value = true
        if (review.value.reply) {
          // TODO edit stuff here
          const {
            values: updatedReply
          } = await marketplaceReviewService.updateReviewReply(
            review.value.reply.id,
            reply.value
          )
          emit('update-review', {
            ...review.value,
            reply: { ...review.value.reply, comment: updatedReply.comment }
          })
          snackbar.success(t('edit_reply_success'))
        } else {
          // new
          const {
            values: updatedReply
          } = await marketplaceReviewService.sendNewReviewReply(
            review.value.id,
            reply.value
          )

          reply.value = updatedReply.comment
          emit('update-review', {
            ...review.value,
            reply: { ...review.value.reply, comment: updatedReply.comment }
          })
          snackbar.success(t('new_reply_success'))
        }
        showReplyForm.value = false
      } catch (error) {
        if (error.response?.data?.data?.errors) {
          errors.value = toCamelCase(error.response.data.data.errors)
        } else {
          handleError(error, snackbar, logger, t)
        }
      } finally {
        internalLoading.value = false
      }
    }

    const approveReview = async () => {
      try {
        emit('set-loading', true)
        await marketplaceAdminService.approveReview(review.value.id)
        snackbar.success('Η αξιολόγηση εγκρίθηκε')
        emit('refetch-reviews')
      } catch (error) {
        handleError(error, snackbar, logger, t)
      } finally {
        emit('set-loading', false)
      }
    }

    const rejectReview = async () => {
      try {
        emit('set-loading', true)
        await marketplaceAdminService.rejectReview(review.value.id)
        snackbar.success('Η αξιολόγηση απορρίφθηκε')
        emit('refetch-reviews')
      } catch (error) {
        handleError(error, snackbar, logger, t)
      } finally {
        emit('set-loading', false)
      }
    }

    const informSeller = async () => {
      try {
        emit('set-loading', true)
        await marketplaceAdminService.informSeller(review.value.id)
        snackbar.success('Ο πωλητής ενημερώθηκε για την αρνητική αξιολόγηση')
        emit('refetch-reviews')
      } catch (error) {
        handleError(error, snackbar, logger, t)
      } finally {
        emit('set-loading', false)
      }
    }

    const onApproveClick = () => {
      confirm.show({
        variant: 'success',
        title: 'Έγκριση αξιολόγησης',
        text:
          'Θέλεις σίγουρα να εγκρίνεις αυτήν την αξιολόγηση; Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.',
        confirmButtonText: 'Έγκριση',
        centered: true,
        size: 'md',
        onConfirm: approveReview
      })
    }

    const onRejectClick = () => {
      confirm.show({
        variant: 'danger',
        title: 'Απόρριψη αξιολόγησης',
        text:
          'Θέλεις σίγουρα να απορρίψεις αυτήν την αξιολόγηση; Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.',
        confirmButtonText: 'Απόρριψη',
        centered: true,
        size: 'md',
        onConfirm: rejectReview
      })
    }

    const onInformSellerClick = () => {
      confirm.show({
        variant: 'warning',
        title: 'Ενημέρωση πωλητή',
        text:
          'Θέλεις σίγουρα να ενημερώσεις τον πωλητή για αυτή την αρνητική αξιολόγηση; Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.',
        confirmButtonText: 'Ενημέρωση',
        centered: true,
        size: 'md',
        onConfirm: informSeller
      })
    }

    const updateComment = async () => {
      try {
        internalLoading.value = true
        const updatedReview = {
          comment: commentEdit.value,
          supportRating: review.value.supportRating,
          deliveryRating: review.value.deliveryRating,
          qualityRating: review.value.qualityRating
        }
        const {
          values: fetchedReview
        } = await marketplaceReviewService.editReview(
          review.value.id,
          updatedReview
        )
        emit('update-review', { ...review.value, ...fetchedReview })
        showEditComment.value = false
        snackbar.success(t('changes_saved'))
      } catch (error) {
        handleError(error, snackbar, logger, t)
      } finally {
        internalLoading.value = false
      }
    }

    const onCancelUpdate = () => {
      showEditComment.value = false
      commentEdit.value = review.value.comment
    }

    const likeReview = async () => {
      try {
        internalLoading.value = true
        const {
          review: likedReview
        } = await marketplaceReviewService.likeReview(review.value.id)
        emit('update-review', likedReview)
      } catch (error) {
        handleError(error, snackbar, logger, t)
      } finally {
        internalLoading.value = false
      }
    }

    const unlikeReview = async () => {
      try {
        internalLoading.value = true
        const {
          review: unlikedReview
        } = await marketplaceReviewService.unlikeReview(review.value.id)
        emit('update-review', unlikedReview)
      } catch (error) {
        handleError(error, snackbar, logger, t)
      } finally {
        internalLoading.value = false
      }
    }

    const onLikeClick = () => {
      review.value.userLiked ? unlikeReview() : likeReview()
    }

    return {
      t,
      tc,
      advantagesString,
      disadvantagesString,
      format,
      ciPositive,
      ciNegative,
      onDeleteClick,
      onDeleteReplyClick,
      isBuyer,
      isSeller,
      showReplyForm,
      reply,
      internalLoading,
      replySchema,
      errors,
      fetchReplySchema,
      onReplySubmit,
      isAdmin,
      faExternalLinkAlt,
      onApproveClick,
      onRejectClick,
      onInformSellerClick,
      showEditComment,
      commentEdit,
      updateComment,
      onCancelUpdate,
      isViewer,
      ciLike,
      likeReview,
      unlikeReview,
      onLikeClick,
      isAnon,
      ciExclamationCircle,
      marketplaceReviewService,
      reportModalVisible,
      reviewIsNonPublicAndOlderThanSevenDays,
      timeDistanceFromNow
    }
  },
  i18n: defineComponentTranslations({
    found_helpful: {
      en:
        '{count} user found this review helpful | {count} users found this review helpful',
      el:
        '{count} χρήστης βρήκε αυτή την αξιολόγηση χρήσιμη | {count} χρήστες βρήκαν αυτή την αξιολόγηση χρήσιμη'
    },
    marketplace_delivery_rating: {
      en: 'Fast delivery',
      el: 'Έγκαιρη παραλαβή'
    },
    marketplace_quality_rating: {
      en: 'Order quality',
      el: 'Ποιότητα παραγγελίας'
    },
    marketplace_support_rating: {
      en: 'Order updates',
      el: 'Ενημέρωση για την εξέλιξη της παραγγελίας'
    },
    delete_review: {
      en: 'Delete review',
      el: 'Διαγραφή αξιολόγησης'
    },
    delete_review_confirm: {
      en: 'Are you sure you want to delete this review?',
      el: 'Θέλεις σίγουρα να διαγράψεις αυτή την αξιολόγηση;'
    },
    delete_reply: {
      en: 'Delete reply',
      el: 'Διαγραφή απάντησης'
    },
    delete_reply_confirm: {
      en: 'Are you sure you want to delete this reply?',
      el: 'Θέλεις σίγουρα να διαγράψεις αυτή την απάντηση;'
    },
    reply_as_store: {
      en: 'Reply as store',
      el: 'Απάντηση καταστήματος'
    },
    new_reply_success: {
      en: 'Your reply has been published',
      el: 'Η απάντηση σου δημοσιεύτηκε'
    },
    edit_reply_success: {
      en: 'Changes were saved successfully',
      el: 'Οι αλλαγές αποθηκεύτηκαν'
    },
    changes_saved: {
      en: 'Changes saved',
      el: 'Οι αλλαγές αποθηκεύτηκαν'
    },
    useful_review: {
      en: 'Useful',
      el: 'Χρήσιμη'
    },
    store_reply: {
      en: 'Reply from the store',
      el: 'Απάντηση καταστήματος'
    }
  })
})
